/**
 * AcceptInvite — public page for /accept-invite/:token.
 *
 * Flow:
 *   1. Lookup invite info (email, role, inviter) by raw token.
 *   2. Collect full_name + password (client-side policy hints).
 *   3. POST /invites/{token}/accept → receive access_token + mfa_setup_required.
 *   4. Begin MFA enrollment → display QR (otpauth URI) + secret + 6-digit input.
 *   5. On 6-digit verify → show backup codes (one-time display) → land in CRM.
 *
 * Zero mock paths. Calls only real endpoints.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, KeyRound, Copy, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PASSWORD_RULES = [
  { test: (p) => p.length >= 12, label: "12+ characters" },
  { test: (p) => /[A-Z]/.test(p), label: "An uppercase letter" },
  { test: (p) => /[a-z]/.test(p), label: "A lowercase letter" },
  { test: (p) => /[0-9]/.test(p), label: "A digit" },
  { test: (p) => /[^A-Za-z0-9]/.test(p), label: "A symbol" },
];

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("loading"); // loading | setup | mfa | codes | done
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [userToken, setUserToken] = useState(null);
  const [mfaBegin, setMfaBegin] = useState(null); // { secret, otpauth_uri }
  const [mfaCode, setMfaCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/invites/${token}`);
        if (!alive) return;
        setInfo(r.data);
        setStage("setup");
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.detail || "Invite invalid");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const pwFails = PASSWORD_RULES.filter(r => !r.test(password));

  const submitSetup = async () => {
    if (pwFails.length) { toast.error("Password does not meet policy"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/invites/${token}/accept`, { password, full_name: fullName });
      setUserToken(r.data.access_token);
      localStorage.setItem("token", r.data.access_token);
      const begin = await axios.post(`${API}/auth/mfa/begin-enrollment`, {}, { headers: { Authorization: `Bearer ${r.data.access_token}` } });
      setMfaBegin(begin.data);
      setStage("mfa");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "weak_password") toast.error(`Weak password: ${d.reasons?.join(", ")}`);
      else toast.error(typeof d === "string" ? d : "Setup failed");
    } finally { setSubmitting(false); }
  };

  const submitMfa = async () => {
    if (!mfaCode || mfaCode.length < 6) { toast.error("Enter the 6-digit code"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/auth/mfa/enroll`, { code: mfaCode }, { headers: { Authorization: `Bearer ${userToken}` } });
      setBackupCodes(r.data.backup_codes || []);
      setStage("codes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally { setSubmitting(false); }
  };

  const finish = () => {
    toast.success("Welcome! You're all set.");
    navigate("/", { replace: true });
  };

  if (loading) {
    return <Centered><Loader2 className="animate-spin" /> Loading invite…</Centered>;
  }
  if (err) {
    return (
      <Centered>
        <div data-testid="accept-invite-error" style={{ color: "#A32D2D", fontWeight: 600 }}>{err}</div>
        <button onClick={() => navigate("/login")} className="btn btn-primary" style={{ marginTop: 14 }}>Go to login</button>
      </Centered>
    );
  }

  return (
    <div data-testid="accept-invite-page" style={{ minHeight: "100vh", background: "#f6f8fb", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "min(440px,100%)", background: "#fff", borderRadius: 14, boxShadow: "0 20px 50px rgba(0,0,0,0.08)", padding: 28 }}>
        {stage === "setup" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <KeyRound style={{ width: 18, height: 18, color: "#2563eb" }} />
              <div style={{ fontSize: 18, fontWeight: 700 }}>Set up your account</div>
            </div>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 18 }}>
              Invited by <strong>{info.inviter_name || "an admin"}</strong> as <strong>{info.role}</strong>.
              Email: <code style={{ background: "#f0f0f0", padding: "1px 6px", borderRadius: 4 }}>{info.email}</code>
            </div>
            <Label>Full name</Label>
            <Input data-testid="accept-invite-fullname" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Jane Doe" />
            <Label>Password</Label>
            <Input data-testid="accept-invite-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Strong password" />
            <ul data-testid="accept-invite-policy" style={{ margin: "6px 0 14px", paddingLeft: 16, fontSize: 11 }}>
              {PASSWORD_RULES.map(r => (
                <li key={r.label} style={{ color: r.test(password) ? "#0F6E56" : "#A32D2D" }}>
                  {r.test(password) ? "✓" : "✗"} {r.label}
                </li>
              ))}
            </ul>
            <button
              data-testid="accept-invite-submit"
              onClick={submitSetup}
              disabled={submitting || pwFails.length > 0 || !fullName.trim()}
              className="btn btn-primary"
              style={{ width: "100%", opacity: submitting || pwFails.length ? 0.6 : 1 }}
            >
              {submitting ? <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> : "Continue — set password"}
            </button>
          </>
        )}
        {stage === "mfa" && mfaBegin && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <ShieldCheck style={{ width: 18, height: 18, color: "#0F6E56" }} />
              <div style={{ fontSize: 18, fontWeight: 700 }}>Enable two-factor (TOTP)</div>
            </div>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 12 }}>
              Scan the code in Google Authenticator, 1Password, or any TOTP app. Or paste the secret manually.
            </div>
            <QRBox uri={mfaBegin.otpauth_uri} />
            <div style={{ fontSize: 11, color: "#666", marginTop: 8, textAlign: "center" }}>
              Secret (paste into app):
              <code data-testid="accept-invite-mfa-secret" style={{ marginLeft: 6, background: "#f0f0f0", padding: "2px 6px", borderRadius: 4 }}>{mfaBegin.secret}</code>
              <button onClick={() => { navigator.clipboard?.writeText(mfaBegin.secret); toast.success("Copied secret"); }} style={{ border: "none", background: "none", cursor: "pointer", verticalAlign: "middle" }}><Copy style={{ width: 11, height: 11 }} /></button>
            </div>
            <Label style={{ marginTop: 14 }}>Enter 6-digit code</Label>
            <Input data-testid="accept-invite-mfa-code" value={mfaCode} maxLength={6} onChange={e => setMfaCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" style={{ letterSpacing: 4, fontSize: 16 }} />
            <button data-testid="accept-invite-mfa-submit" onClick={submitMfa} disabled={submitting} className="btn btn-primary" style={{ width: "100%", marginTop: 12 }}>
              {submitting ? <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> : "Verify & enable 2FA"}
            </button>
          </>
        )}
        {stage === "codes" && backupCodes && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <CheckCircle2 style={{ width: 18, height: 18, color: "#0F6E56" }} />
              <div style={{ fontSize: 18, fontWeight: 700 }}>Save your backup codes</div>
            </div>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 12 }}>
              Each code can be used once if you lose access to your authenticator. Store them in a password manager now — they won't be shown again.
            </div>
            <div data-testid="accept-invite-backup-codes" style={{ background: "#f9f9f9", border: "0.5px solid #eee", borderRadius: 8, padding: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontFamily: "monospace", fontSize: 12 }}>
              {backupCodes.map(c => <div key={c}>{c}</div>)}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button onClick={() => { navigator.clipboard?.writeText(backupCodes.join("\n")); toast.success("Copied all"); }} className="btn" style={{ fontSize: 12, flex: 1 }}>Copy all</button>
              <button data-testid="accept-invite-finish" onClick={finish} className="btn btn-primary" style={{ fontSize: 12, flex: 1 }}>I've saved them — Continue</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", padding: 24, background: "#f6f8fb" }}>{children}</div>;
}
function Label({ children, style }) {
  return <div style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4, marginTop: 10, ...(style || {}) }}>{children}</div>;
}
function Input({ style, ...props }) {
  return <input {...props} style={{ width: "100%", padding: "8px 10px", fontSize: 13, border: "0.5px solid #bbb", borderRadius: 6, fontFamily: "inherit", ...(style || {}) }} />;
}
function QRBox({ uri }) {
  // Use Google Chart API for QR (no extra dep). Falls back to the raw URI if the image fails.
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(uri)}`;
  return (
    <div data-testid="accept-invite-qr" style={{ display: "flex", justifyContent: "center", padding: 12 }}>
      <img src={src} alt="TOTP QR" width={180} height={180} style={{ borderRadius: 8, background: "#fff" }} />
    </div>
  );
}
